import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dispatch } from 'redux';
import {
  Dashboard,
  Summary,
  getDashboard,
  getSummary,
  StepName,
  getAvailableOptionsForOrder,
  pauseLockTimer,
  AvailableOptions
} from '|/app/services';
import {
  GET_AVAILABLE_OPTIONS_FOR_ORDER,
  GET_DASHBOARD,
  ORDER_SUMMARY_FETCH
} from '|/ducks/dashboard/action-types';

interface VehicleLock {
  isAvailable?: boolean | null;
  expiresOn?: string;
}

interface Terms {
  amountFinanced?: number;
  apr?: number;
  downPayment?: number;
  monthlyPayment?: number;
  termMonths?: number;
}

interface OldStep {
  isEnabled: boolean;
  status: string;
  subStatus: string;
}

interface OldSteps {
  additionalIncomeDocumentation?: OldStep;
  bankAccountValidation?: OldStep;
  contracts?: OldStep;
  demographics?: OldStep;
  driversLicense?: OldStep;
  gapWaiver?: OldStep;
  identity?: OldStep;
  identityVerification?: OldStep;
  order?: OldStep;
  paymentCollection?: OldStep;
  paymentOption?: OldStep;
  paymentSchedule?: OldStep;
  proofOfIncome?: OldStep;
  proofOfResidency?: OldStep;
  protection?: OldStep;
  tradeDocumentation?: OldStep;
  tradeIn?: OldStep;
  transferMethod?: OldStep;
  transferSchedule?: OldStep;
  insuranceOptions?: OldStep;
  underwriting?: OldStep;
  verifications?: OldStep;
}

interface Status {
  orderState: string;
  orderId: string;
  steps: OldSteps;
}

interface OldMarketing {
  hashedEmailAddress?: string | null;
  hashedFirstName?: string | null;
  hashedLastName?: string | null;
  hashedDateOfBirth?: string | null;
  hashedBirthMonthAndDay?: string | null;
  hashedBirthYear?: string | null;
  hashedCity?: string | null;
  hashedState?: string | null;
  hashedZip5?: string | null;
  hashedPhoneNumber?: string | null;
}

interface OldDashboard {
  errorMessage: string;
  errors: string[];
  experiments: { [key: string]: string };
  status?: Status;
  terms?: Terms;
  vehicle?: Vehicle;
  vehicleLock?: VehicleLock;
  marketing?: OldMarketing;
  canRollback: boolean;
}

interface AncillaryProduct {
  type: string;
  price?: number;
  months?: number;
  mileage?: number;
  declined: boolean;
}

interface Address {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

interface Customer {
  emailAddress?: string | null;
  homeAddress?: Address;
  deliveryAddress?: Address;
  name: string;
  phoneNumber?: string | null;
}

interface Discount {
  amount?: number;
  description?: string | null;
}

interface Fee {
  amount?: number;
  description?: string | null;
}

interface Tax {
  amount?: number;
  description?: string | null;
}

interface Rebate {
  name?: string | null;
  amount?: number;
}

interface DeliveryCharge {
  amount?: number;
  type?: string;
}

interface Quote {
  discounts?: Discount[] | null;
  fees?: Fee[] | null;
  taxes?: Tax[] | null;
  rebates?: Rebate[] | null;
  deliveryCharge?: DeliveryCharge;
  localDeliveryCharge?: number;
  totalPrice?: number;
  tradeInCredit?: number;
  totalTaxes?: number;
  totalTaxesWithoutTrade?: number;
}

interface Trade {
  lienAmount?: number;
  value?: number;
  year?: number;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  declined: boolean;
  lienType: string | null;
  taxSavings: number;
  odometer?: number;
  acquisitionName?: string | null;
  acquisitionId?: number;
}

interface Vehicle {
  engine?: string;
  make?: string | null;
  mileage?: number;
  model?: string | null;
  marketplaceDealerId?: number;
  bodyType: string;
  price?: number;
  totalPrice?: number;
  stockNumber: number;
  transmission: string;
  fuelDescription: string;
  trim?: string | null;
  vehicleId?: number;
  year?: number | null;
  vin?: string | null;
  isAvailable?: boolean | null;
  inventoryType: string;
  desktopImageUrl: string;
  mobileImageUrl: string;
  purchaseType: string;
  bodyTypeImageUrl: string;
  makeImageUrl: string;
}

interface OrderSummaryVehicle {
  engine?: string;
  make?: string | null;
  mileage?: number;
  model?: string | null;
  marketplaceDealerId?: number;
  bodyType: string;
  price?: number;
  totalPrice?: number;
  stockNumber: number;
  transmission: string;
  fuelDescription: string;
  trim?: string | null;
  vehicleId?: number;
  year?: number;
  vin?: string | null;
  isAvailable?: boolean | null;
  inventoryType: string;
  desktopImageUrl: string;
  mobileImageUrl: string;
  purchaseType: string;
  bodyTypeImageUrl: string;
  makeImageUrl: string;
}

interface DownPaymentDetails {
  totalCashDue?: number;
  shippingFee?: number;
  cashDown?: number;
  tradeDown?: number;
}

interface AutoPay {
  canEnroll: boolean | null;
  isEnrolled?: boolean | null;
}

interface SelfArrangedFinancePaymentBreakdown {
  shippingFee?: number;
  cash?: number;
  approvedLoanAmount?: number | null;
  loanAmount?: number;
}

interface PaymentMethod {
  method?: string | null;
  institutionName?: string | null;
  amountFinanced?: number;
  downPaymentDetails: DownPaymentDetails;
  totalDown?: number;
  apr?: number;
  monthlyPayment?: number;
  term?: number;
  lienHolder?: string | null;
  autoPay: AutoPay;
  safPaymentBreakdown: SelfArrangedFinancePaymentBreakdown;
}

interface Schedule {
  deliveryPreference: string;
  serviceDisplayName: string | null;
  serviceTimeZone?: string | null;
  scheduleStartDateTimeUtc?: string | null;
  scheduleStartDateTimeLocal?: string | null;
  address: Address;
  deliveryType?: string | null;
  locationShortCode: string | null;
  scheduledTimeSlot?: string | null;
  scheduledTimeWindow?: LocalTimeWindow;
}

interface LocalTimeWindow {
  startTime?: string | null;
  endTime?: string | null;
}

interface CoApplicant {
  name: string;
  email?: string | null;
  isEligible?: boolean;
  hasCoApplicantToggleOn?: boolean;
}

interface OldSummary {
  ancillaryProducts: AncillaryProduct[];
  customer?: Customer;
  quote?: Quote;
  trade?: Trade;
  vehicle?: OrderSummaryVehicle;
  paymentMethod?: PaymentMethod;
  schedule?: Schedule;
  orderPlacedOnUtc?: string | null;
  coBuyer?: CoApplicant;
}

const dispatchDashboardEvent = (
  dispatch: Dispatch<any>,
  dashboard: OldDashboard
) => {
  dispatch({
    type: `${GET_DASHBOARD}_SUCCESS`,
    payload: dashboard
  });
};

const dispatchSummaryEvent = (dispatch: Dispatch<any>, summary: OldSummary) => {
  dispatch({
    type: `${ORDER_SUMMARY_FETCH}_SUCCESS`,
    payload: summary
  });
};

const dispatchAvailableOptionsEvent = (
  dispatch: Dispatch<any>,
  availableOptions: any
) => {
  dispatch({
    type: `${GET_AVAILABLE_OPTIONS_FOR_ORDER}_SUCCESS`,
    payload: availableOptions
  });
};

const toOldStep = (step: { isEnabled: boolean; status: string }): OldStep => {
  return {
    isEnabled: step.isEnabled,
    status: step.status,
    subStatus: ''
  };
};

const toOldDashboard = (
  dashboard: Dashboard | null,
  summary: Summary | null
): OldDashboard => {
  return {
    status: {
      orderId: '',
      orderState: '',
      steps: {
        additionalIncomeDocumentation: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.AdditionalIncome) || {
            isEnabled: false,
            status: ''
          }
        ),
        bankAccountValidation: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.BAV) || {
            isEnabled: false,
            status: ''
          }
        ),
        contracts: toOldStep({
          isEnabled: false,
          status: ''
        }),
        demographics: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.PersonalDetails) || {
            isEnabled: false,
            status: ''
          }
        ),
        driversLicense: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.DL) || {
            isEnabled: false,
            status: ''
          }
        ),
        gapWaiver: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.Gap) || {
            isEnabled: false,
            status: ''
          }
        ),
        identity: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.Identity) || {
            isEnabled: false,
            status: ''
          }
        ),
        identityVerification: toOldStep(
          dashboard?.steps?.find(
            s => s.id === StepName.IdentityVerification
          ) || {
            isEnabled: false,
            status: ''
          }
        ),
        insuranceOptions: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.Insurance) || {
            isEnabled: false,
            status: ''
          }
        ),
        order: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.Review) || {
            isEnabled: false,
            status: ''
          }
        ),
        paymentCollection: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.Payment) || {
            isEnabled: false,
            status: ''
          }
        ),
        paymentOption: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.CoF) || {
            isEnabled: false,
            status: ''
          }
        ),
        paymentSchedule: toOldStep({
          isEnabled: false,
          status: ''
        }),
        proofOfIncome: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.Income) || {
            isEnabled: false,
            status: ''
          }
        ),
        proofOfResidency: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.Address) || {
            isEnabled: false,
            status: ''
          }
        ),
        protection: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.VSC) || {
            isEnabled: false,
            status: ''
          }
        ),
        tradeDocumentation: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.TradeVerification) || {
            isEnabled: false,
            status: ''
          }
        ),
        tradeIn: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.TradeIn) || {
            isEnabled: false,
            status: ''
          }
        ),
        transferMethod: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.PickupDelivery) || {
            isEnabled: false,
            status: ''
          }
        ),
        transferSchedule: toOldStep(
          dashboard?.steps?.find(s => s.id === StepName.Schedule) || {
            isEnabled: false,
            status: ''
          }
        ),
        underwriting: toOldStep({
          isEnabled: false,
          status: ''
        }),
        verifications: toOldStep({
          isEnabled: false,
          status: ''
        })
      }
    },
    errorMessage: '',
    errors: [],
    experiments: {},
    terms: {
      amountFinanced: summary?.funding?.loan?.amountFinanced,
      apr: summary?.funding?.loan?.apr,
      downPayment: summary?.funding?.loan?.down,
      monthlyPayment: summary?.funding?.loan?.payment,
      termMonths: summary?.funding?.loan?.term
    },
    vehicle: {
      ...summary?.vehicle,
      engine: summary?.vehicle?.engineDescription,
      mileage: summary?.vehicle?.odometer,
      bodyType: '',
      price: summary?.totals?.vehicleStickerPrice,
      totalPrice: summary?.totals?.totalPrice,
      stockNumber: NaN,
      transmission: '',
      fuelDescription: '',
      vehicleId: summary?.vehicle?.id,
      inventoryType: '',
      desktopImageUrl: '',
      mobileImageUrl: '',
      purchaseType: '',
      bodyTypeImageUrl: '',
      makeImageUrl: ''
    },
    vehicleLock: {
      expiresOn: summary?.vehicle?.unlockDateTime,
      isAvailable: summary?.vehicle?.isAvailable
    },
    marketing: {
      hashedBirthMonthAndDay: summary?.marketing?.hashedBirthMonthAndDay,
      hashedBirthYear: summary?.marketing?.hashedBirthYear,
      hashedCity: summary?.marketing?.hashedCity,
      hashedEmailAddress: summary?.marketing?.hashedEmailAddress,
      hashedFirstName: summary?.marketing?.hashedFirstName,
      hashedLastName: summary?.marketing?.hashedLastName,
      hashedPhoneNumber: summary?.marketing?.hashedPhoneNumber,
      hashedState: summary?.marketing?.hashedState,
      hashedZip5: summary?.marketing?.hashedZip5,
      hashedDateOfBirth: summary?.marketing?.hashedDateOfBirth
    },
    canRollback:
      summary?.transfer?.scheduledTimeSlot === undefined
        ? false
        : dayjs()
            .add(2, 'day')
            .isBefore(dayjs(summary.transfer.windowStartTime))
  };
};

const toOldSummary = (summary: Summary | null): OldSummary => {
  const ancillaryProducts = summary?.product
    ? [
        {
          price: summary?.product.price,
          mileage: summary?.product.mileage,
          months: summary?.product.months,
          declined: false,
          type: ''
        }
      ]
    : [];
  return {
    ancillaryProducts: ancillaryProducts,
    customer: {
      name: [
        summary?.customer?.firstName,
        summary?.customer?.middleName,
        summary?.customer?.lastName,
        summary?.customer?.suffix
      ]
        .filter(Boolean)
        .join(' '),
      homeAddress: {
        addressLine1: summary?.customer?.address?.line1,
        addressLine2: summary?.customer?.address?.line2,
        city: summary?.customer?.address?.city,
        state: summary?.customer?.address?.state,
        zipCode: summary?.customer?.address?.zipCode
      },
      phoneNumber: summary?.customer?.phoneNumber,
      emailAddress: summary?.customer?.emailAddress
    },
    quote: {
      discounts: summary?.discounts,
      fees: summary?.fees,
      taxes: null,
      rebates: summary?.rebates,
      totalPrice: summary?.totals?.totalPrice,
      deliveryCharge: {
        amount: summary?.totals?.deliveryCharge
      },
      localDeliveryCharge: summary?.totals?.deliveryCharge,
      tradeInCredit: summary?.totals?.tradeInValue,
      totalTaxes: summary?.totals?.taxes,
      totalTaxesWithoutTrade: summary?.totals?.taxesWithoutTrade
    },
    trade: {
      lienAmount: summary?.tradeIn?.lienAmount,
      value: summary?.tradeIn?.value,
      year: summary?.tradeIn?.year,
      make: summary?.tradeIn?.make,
      model: summary?.tradeIn?.model,
      trim: summary?.tradeIn?.trim,
      declined: summary?.tradeIn?.status === 'Declined',
      lienType: null,
      taxSavings:
        (summary?.totals?.taxesWithoutTrade ?? 0) -
          (summary?.totals?.taxes ?? 0) ?? 0,
      odometer: summary?.tradeIn?.odometer,
      acquisitionName: summary?.tradeIn?.acquisitionName,
      acquisitionId: summary?.tradeIn?.acquisitionId
    },
    vehicle: {
      ...summary?.vehicle,
      engine: summary?.vehicle?.engineDescription,
      mileage: summary?.vehicle?.odometer,
      bodyType: '',
      price: summary?.totals?.vehicleStickerPrice,
      totalPrice: summary?.totals?.totalPrice,
      stockNumber: NaN,
      transmission: '',
      fuelDescription: '',
      vehicleId: summary?.vehicle?.id,
      inventoryType: '',
      desktopImageUrl: '',
      mobileImageUrl: '',
      purchaseType: '',
      bodyTypeImageUrl: '',
      makeImageUrl: ''
    },
    paymentMethod: {
      method: summary?.funding?.type,
      institutionName:
        summary?.funding?.institution &&
        summary?.funding?.institution.trim() !== ''
          ? summary?.funding?.institution
          : summary?.funding?.thirdParty?.lenderName,
      amountFinanced: summary?.funding?.loan?.amountFinanced,
      downPaymentDetails: {
        totalCashDue: summary?.funding?.loan?.details?.cashDown,
        shippingFee: summary?.funding?.loan?.details?.shippingFee,
        cashDown: summary?.funding?.loan?.details?.cashDown,
        tradeDown: summary?.funding?.loan?.details?.tradeDown
      },
      totalDown: summary?.funding?.loan?.down,
      apr: summary?.funding?.loan?.apr,
      monthlyPayment: summary?.funding?.loan?.payment,
      term: summary?.funding?.loan?.term,
      lienHolder: summary?.funding?.thirdParty?.lenderName,
      autoPay: {
        canEnroll: null,
        isEnrolled: summary?.enrolledInAutopay
      },
      safPaymentBreakdown: {
        shippingFee: summary?.funding?.thirdParty?.breakdown?.shippingFee,
        cash: summary?.funding?.thirdParty?.breakdown?.cash,
        approvedLoanAmount: null,
        loanAmount: summary?.funding?.thirdParty?.breakdown?.loanAmount
      }
    },
    schedule: {
      deliveryPreference: summary?.transfer?.deliveryType?.includes('Delivery')
        ? 'delivery'
        : 'pick-up',
      serviceDisplayName: null,
      serviceTimeZone: summary?.transfer?.serviceTimeZone,
      scheduleStartDateTimeUtc:
        summary?.transfer?.windowStartTime ??
        summary?.transfer?.scheduledTimeSlot,
      scheduleStartDateTimeLocal: summary?.transfer?.windowStartTime
        ? dayjs(summary?.transfer?.windowStartTime).format()
        : summary?.transfer?.scheduledTimeSlot
        ? summary?.transfer?.scheduledTimeSlot
        : null,
      address: {
        addressLine1: summary?.transfer?.address?.line1,
        addressLine2: summary?.transfer?.address?.line2,
        city: summary?.transfer?.address?.city,
        state: summary?.transfer?.address?.state,
        zipCode: summary?.transfer?.address?.zipCode
      },
      deliveryType: summary?.transfer?.deliveryType,
      locationShortCode: null,
      scheduledTimeSlot: summary?.transfer?.scheduledTimeSlot,
      scheduledTimeWindow: {
        startTime: summary?.transfer?.windowStartTime,
        endTime: summary?.transfer?.windowEndTime
      }
    },
    orderPlacedOnUtc: summary?.orderPlacedDateTime,
    coBuyer: {
      name: [
        summary?.cobuyer?.firstName,
        summary?.cobuyer?.middleName,
        summary?.cobuyer?.lastName,
        summary?.cobuyer?.suffix
      ]
        .filter(Boolean)
        .join(' '),
      email: summary?.cobuyer?.emailAddress,
      isEligible: summary?.financing?.isEligibleForCoApplicant,
      hasCoApplicantToggleOn: summary?.financing?.isCoApplicantToggleOn
    }
  };
};

const useDashboardViewModel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null as null | Dashboard);
  const [orderSummary, setOrderSummary] = useState(null as null | Summary);

  const ReloadDashboard = useCallback(async () => {
    return Promise.all([
      getDashboard(),
      getSummary(),
      getAvailableOptionsForOrder()
    ]).then(async ([newDashboard, newSummary, newAvailableOptions]) => {
      if (newDashboard === null) {
        navigate('/vehicle/missing');
      }
      dispatchDashboardEvent(
        dispatch,
        toOldDashboard(newDashboard, newSummary)
      );
      dispatchSummaryEvent(dispatch, toOldSummary(newSummary));
      dispatchAvailableOptionsEvent(dispatch, newAvailableOptions);

      setDashboard(newDashboard);
      setOrderSummary(newSummary);
      if (
        !newAvailableOptions?.needsToScheduleTimeSlot &&
        !newAvailableOptions?.isVehicleLockPaused
      ) {
        await pauseLockTimer();
      }
    });
  }, [dispatch, navigate]);

  const UpdateDashboardAndSummary = useCallback(
    (
      newDashboard: Dashboard,
      newSummary: Summary,
      newAvailableOptions: AvailableOptions
    ) => {
      const asyncPauseLockTimer = async () => {
        if (
          !newAvailableOptions?.needsToScheduleTimeSlot &&
          !newAvailableOptions?.isVehicleLockPaused
        ) {
          await pauseLockTimer();
        }
      };

      dispatch({ type: `${GET_DASHBOARD}_LOADING` });

      dispatchDashboardEvent(
        dispatch,
        toOldDashboard(newDashboard, newSummary)
      );
      dispatchSummaryEvent(dispatch, toOldSummary(newSummary));
      dispatchAvailableOptionsEvent(dispatch, newAvailableOptions);

      setDashboard(newDashboard);
      setOrderSummary(newSummary);

      asyncPauseLockTimer();
    },
    [dispatch]
  );

  const GetDashboardAndRouteToNextStep = useCallback(() => {
    navigate('/next-step');
  }, [navigate]);

  return {
    dashboard,
    orderSummary,
    ReloadDashboard,
    UpdateDashboardAndSummary,
    GetDashboardAndRouteToNextStep
  };
};

export default useDashboardViewModel;
